import {rootIds, getElementTypeAndDataType} from './rootIds';
import {Types, Plugins} from '@packages/systems/wfdl';
import type {JSONNode} from '@packages/shared/types/JSONNode';
import {fixDataTypeJson} from '@packages/systems/core/component-migrations';
import {cloneDeep} from 'lodash';
import {generateGuid} from '@packages/utilities/guid';

export const fixSymbolRootAndInstDataTypeJson = (jsonNode: JSONNode): void => {
  const {data = {}, type = null} = jsonNode;
  const hasDataType = Boolean(jsonNode.dataType);
  const hasInstDataType = Boolean(type === 'Symbol' && data.sym.instDataType);

  if (hasDataType) {
    // @ts-expect-error - TS2345 - Argument of type 'string | Type | undefined' is not assignable to parameter of type 'Type'.
    jsonNode.dataType = fixDataTypeJson(jsonNode.dataType);
  }

  if (hasInstDataType) {
    data.sym.instDataType = fixDataTypeJson(data.sym.instDataType);
    jsonNode.data = data;
  }
};

export const hasFlattenedOverrides = (
  overrideValue: unknown
): overrideValue is string[] =>
  Array.isArray(overrideValue) &&
  overrideValue.every((o) => typeof o === 'string');

export const isJSONNode = (value: unknown): value is JSONNode => {
  return (
    typeof value === 'object' &&
    value !== null &&
    '_id' in value &&
    typeof value._id === 'string'
  );
};

/**
 * Returns true if the override value is an array of JSONNode.
 *
 * **Note** that this is not the same as `!hasFlattenedOverrides(value)` because
 * that would return true for any value that is not an array of strings.
 */
export const hasUnflattenedOverrides = (
  overrideValue: unknown
): overrideValue is Array<JSONNode> =>
  Array.isArray(overrideValue) && overrideValue.every(isJSONNode);

export const getOverrideFieldsNeedingFlattening = (
  plugins: Plugins.PluginsType,
  dataTypes: Plugins.DataTypes,
  jsonNode: JSONNode
): Array<string> | null => {
  const {type} = jsonNode;
  if (!type) return null;

  const overrides:
    | undefined
    | {
        [key: string]: Array<JSONNode>;
      } = jsonNode.data?.sym?.overrides;
  const hasOverrides = Boolean(type === 'Symbol' && overrides);

  if (!hasOverrides) return null;

  const overridesFieldsNeedingFlattening: Array<string> = [];

  const [_, dataType] = getElementTypeAndDataType(
    plugins,
    dataTypes,
    type,
    jsonNode.data?.sym?.inst
  );

  const overrideFieldsTypes = dataType && Types.getRecordFields(dataType);

  if (!overrideFieldsTypes) return null;

  for (const overrideKey in overrides) {
    const overrideFieldType = overrideFieldsTypes[overrideKey];

    // Make sure we're working with a variation of List of Elements (i.g: RichTextChildren, List<Element>)
    if (
      // @ts-expect-error - TS2345 - Argument of type 'Type | undefined' is not assignable to parameter of type 'Type'.
      Types.isListOfElements(overrideFieldType) &&
      hasUnflattenedOverrides(overrides[overrideKey])
    ) {
      overridesFieldsNeedingFlattening.push(overrideKey);
    }
  }

  return overridesFieldsNeedingFlattening;
};

/**
 * Re-guids JSONNodes and their children that have duplicate ids
 */
export const reGuidOverridesArray = (
  overridesArray: Array<JSONNode>,
  allJsonNodeAndOverrideIds: Set<string>
): Array<JSONNode> => {
  // A map of old to new children `_id`s to reassign as we encounter them.
  const childrenIds: Map<string, string> = new Map();

  return overridesArray.map((jsonNode) => {
    const childId = childrenIds.get(jsonNode._id);

    // Loop through `children` array and if we have duplicates, assign them unique guids.
    if (Array.isArray(jsonNode.children) && jsonNode.children.length) {
      jsonNode.children = jsonNode.children.map((id) => {
        if (allJsonNodeAndOverrideIds.has(id)) {
          const newGuid = generateGuid();
          childrenIds.set(id, newGuid);
          id = newGuid;
        }

        return id;
      });
    }

    // If the current jsonNode is a child of another node in the array, assign it
    // the new guid created from an earlier iteration.
    if (childId) {
      jsonNode._id = childId;
    } else {
      const isDuplicate = allJsonNodeAndOverrideIds.has(jsonNode._id);
      if (isDuplicate) jsonNode._id = generateGuid();
    }

    allJsonNodeAndOverrideIds.add(jsonNode._id);

    return jsonNode;
  });
};

/**
 * Takes a map of overrides and returns a tuple that includes a reference to
 * each JSONNode `_id` and an array of flattened nodes.
 * i.g: [flattenedOverridesRootIds, flattenedOverridesArray]
 */
export const flattenOverrides = (
  plugins: Plugins.PluginsType,
  dataTypes: Plugins.DataTypes,
  jsonNode: JSONNode,
  overridesFieldsNeedingFlattening: Array<string>,
  allJsonNodeAndOverrideIds: Set<string>
): {
  flattenedOverridesRootIds: {[key: string]: Array<string>};
  flattenedOverridesArray: Array<JSONNode>;
} =>
  overridesFieldsNeedingFlattening.reduce(
    (acc, overrideKey) => {
      const {data = {}} = jsonNode;

      /**
       * Checks the current jsonNode's overrides against our
       * `allJsonNodeAndOverrideIds` Set to see if we have duplicates, if so, we'll
       * assign new ids to the `overridesArray`.
       */
      const overridesArray = reGuidOverridesArray(
        data.sym.overrides[overrideKey],
        allJsonNodeAndOverrideIds
      );

      /**
       * Reassigns each override field's JSONNodes with references to their `_id`s.
       *
       * data.sym.overrides: {
       *   'myOverrideField1': [
       *     'childNodeReferenceId1',
       *     'childNodeReferenceId2',
       *   ],
       *   'myOverrideField2': [
       *     'childNodeReferenceId3',
       *     'childNodeReferenceId4',
       *   ],
       * }
       */
      acc.flattenedOverridesRootIds[overrideKey] = rootIds(
        plugins,
        dataTypes
      )(overridesArray);

      /**
       * Flattens each override field into an array to insert into the JSONNode tree.
       */
      acc.flattenedOverridesArray =
        acc.flattenedOverridesArray.concat(overridesArray);

      return acc;
    },
    {flattenedOverridesRootIds: {}, flattenedOverridesArray: []} as {
      flattenedOverridesRootIds: {[key: string]: Array<string>};
      flattenedOverridesArray: Array<JSONNode>;
    }
  );

export const migrateFlattenOverrides = (
  plugins: Plugins.PluginsType,
  dataTypes: Plugins.DataTypes,
  jsonNodes: Array<JSONNode>
): Array<JSONNode> => {
  const newJsonNodesTreeIncludingOverrides: Array<JSONNode> = [];
  // This Set contains all JSONNode and Override `_id`s within in the provided
  // `jsonNodes` array. We first assign the nodes we currently have and we'll
  // continue add to this set as we encounter overrides.
  const allJsonNodeAndOverrideIds: Set<string> = new Set(
    jsonNodes.map((n) => n._id)
  );

  for (const jsonNode of jsonNodes) {
    fixSymbolRootAndInstDataTypeJson(jsonNode);

    const overridesFieldsNeedingFlattening = getOverrideFieldsNeedingFlattening(
      plugins,
      dataTypes,
      jsonNode
    );

    if (
      overridesFieldsNeedingFlattening &&
      overridesFieldsNeedingFlattening.length
    ) {
      const jsonNodeCopy: JSONNode = cloneDeep(jsonNode);
      const {data = {}} = jsonNodeCopy;

      /**
       * If this JSONNode has overrides needing to be flattened, replace the
       * JSONNode's `data.sym.overrides` property with a flattened list of node ids
       * and append the nodes to the root tree.
       */
      const {flattenedOverridesRootIds, flattenedOverridesArray} =
        flattenOverrides(
          plugins,
          dataTypes,
          jsonNodeCopy,
          overridesFieldsNeedingFlattening,
          allJsonNodeAndOverrideIds
        );

      for (const override in flattenedOverridesRootIds) {
        data.sym.overrides[override] = flattenedOverridesRootIds[override];
      }

      newJsonNodesTreeIncludingOverrides.push(
        jsonNodeCopy,
        ...flattenedOverridesArray
      );
    } else {
      /**
       * Else, add the current jsonNode to the tree.
       */
      newJsonNodesTreeIncludingOverrides.push(jsonNode);
    }
  }

  return newJsonNodesTreeIncludingOverrides;
};
